import React, { useState } from 'react'
import {
  ReservationCalendar,
  ReservationCalendarDay,
  StatusLegend,
  Alert,
} from '@pandiafi/components'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { Loading } from '@pandiafi/design-system'

import type {
  TimeSlotForResident,
  ReservationCalendarTimeSlotsResponse,
} from '../../../common/validations/reservationCalendarSchemas'
import { useAPIGetReservationCalendarTimeSlots } from '../../api/apiHooks/useAPIGetReservationCalendarTimeSlots'
import { Id, SlotStatus } from '../../../common/types'
import { CancelReservationModal } from './CancelReservationModal'
import { SingleReservationModal } from './SingleReservationModal'
import { ResidentAppSlot, ResidentAppTimeSlotType } from './ResidentAppSlot'
import { WEEKDAYS } from './constants'
import {
  formatTimeForCalendar,
  getSlotSizeMinutes,
  groupTimeSlotsIntoPrivateSlots,
} from './utils'
import { formatTime } from '../../utils/dateUtils'
import type {
  ReservationCalendarForResident,
  PriceInfoForDate,
  CalculatedPrivateSlot,
} from './types'
import { Contract } from '../../../common/validations'
import { ResidentDetailsHtml } from '../../components/ResidentDetailsHtml/ResidentDetailsHtml'
import { pickResidentDetails } from '../../utils/residentDetails'

import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'

interface ReservationCalendarGridProps {
  calendar: ReservationCalendarForResident
  startDate: string
  endDate: string
  onTimeSlotSelection: (
    timeSlots: TimeSlotForResident[],
    priceInfo: PriceInfoForDate
  ) => void
  timeSlotsData?: ReservationCalendarTimeSlotsResponse
  contract: Contract
  selectedTimeSlotsId?: Id[]
  readonly?: boolean
}

export const ReservationCalendarGrid: React.FC<
  ReservationCalendarGridProps
> = ({
  calendar,
  startDate,
  endDate,
  onTimeSlotSelection,
  timeSlotsData: preFetchedData,
  contract,
  readonly = false,
  selectedTimeSlotsId,
}) => {
  const { currentContractId } = useCurrentContract()
  const { t, i18n } = useTranslation(['reservations', 'errors'])
  const lang = i18n.language
  const [cancelModalState, setCancelModalState] = useState<{
    show: boolean
    timeSlot: TimeSlotForResident | null
  }>({
    show: false,
    timeSlot: null,
  })
  const [singleReservationModalState, setSingleReservationModalState] =
    useState<{
      show: boolean
      timeSlot: TimeSlotForResident | null
    }>({
      show: false,
      timeSlot: null,
    })

  const apiQuery = useAPIGetReservationCalendarTimeSlots(
    currentContractId,
    String(calendar.id),
    startDate,
    endDate,
    { enabled: !preFetchedData }
  )

  const timeSlotsData = preFetchedData || apiQuery.data

  if (apiQuery.isError) {
    return (
      <Alert variant="warning" className="my-4">
        {t('errors:other.internalError')}
      </Alert>
    )
  }

  if (!timeSlotsData) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loading variant="details" />
      </div>
    )
  }

  const timeSlots = timeSlotsData.timeSlots || []
  const meta = timeSlotsData.calendarMetadata
  const groupSizeByWeekday: Record<number, number> = {
    1: meta.slotsPerPrivateReservationMon,
    2: meta.slotsPerPrivateReservationTue,
    3: meta.slotsPerPrivateReservationWed,
    4: meta.slotsPerPrivateReservationThu,
    5: meta.slotsPerPrivateReservationFri,
    6: meta.slotsPerPrivateReservationSat,
    7: meta.slotsPerPrivateReservationSun,
  }

  const slotSizeMinutes = getSlotSizeMinutes(meta.weeklySlotDuration)

  const dayStartTime = formatTimeForCalendar(meta.reservableTimeStart)
  const dayEndTime = formatTimeForCalendar(meta.reservableTimeEnd)

  const timeSlotsByWeekday: Record<number, TimeSlotForResident[]> = {}
  for (const slot of timeSlots) {
    const weekday = DateTime.fromISO(slot.startsAt).weekday
    if (!timeSlotsByWeekday[weekday]) timeSlotsByWeekday[weekday] = []
    timeSlotsByWeekday[weekday].push(slot)
  }

  const rangeStart = DateTime.fromISO(startDate).startOf('day')
  const rangeEnd = DateTime.fromISO(endDate).endOf('day')

  const now = DateTime.now()

  const weekdays: Array<{
    weekday: number
    weekdayName: string
    privateSlots: CalculatedPrivateSlot[]
  }> = WEEKDAYS.map(({ weekday, translationKey }) => {
    const daySlots = timeSlotsByWeekday[weekday] ?? []
    const groupSize = groupSizeByWeekday[weekday] ?? 1
    const calculatedSlots = groupTimeSlotsIntoPrivateSlots(daySlots, groupSize)
    const privateSlots: CalculatedPrivateSlot[] = []
    for (const slot of calculatedSlots) {
      const startsAt = DateTime.fromISO(slot.startsAt)
      if (startsAt < rangeStart || startsAt > rangeEnd) continue
      if (startsAt < now && slot.status === SlotStatus.FREE) continue
      if (!privateSlots.find((s) => s.startsAt === slot.startsAt)) {
        privateSlots.push(slot)
      }
    }
    return { weekday, weekdayName: t(translationKey), privateSlots }
  })

  const isSlotOutsideContractPeriod = (privateSlot: CalculatedPrivateSlot) => {
    const slotStartDate = DateTime.fromISO(privateSlot.startsAt).toISODate()

    if (!slotStartDate) {
      return false
    }

    if (slotStartDate < contract.startDate) {
      return true
    }

    return contract.endDate !== null && slotStartDate > contract.endDate
  }

  const handlePrivateSlotClick = (privateSlot: CalculatedPrivateSlot) => {
    const slotDateTime = DateTime.fromISO(privateSlot.startsAt)
    const slotDate = slotDateTime.toISODate() ?? ''
    const priceInfo = timeSlotsData.pricesByDate?.[slotDate]

    if (!priceInfo) {
      return
    }

    if (slotDateTime < now) {
      return
    }

    if (privateSlot.status === SlotStatus.RESERVED) {
      return
    }

    if (
      privateSlot.status === SlotStatus.OWN ||
      privateSlot.status === SlotStatus.OWN_RECURRING
    ) {
      const hasSingleReservation = privateSlot.reservations.some(
        (r) => !r.reservationRecurrenceId
      )

      if (hasSingleReservation) {
        setSingleReservationModalState({
          show: true,
          timeSlot: privateSlot.timeSlots[0],
        })
      } else {
        setCancelModalState({
          show: true,
          timeSlot: privateSlot.timeSlots[0],
        })
      }
      return
    }

    onTimeSlotSelection(privateSlot.timeSlots, priceInfo)
  }

  const getTimeSlotType = (
    privateSlot: CalculatedPrivateSlot
  ): ResidentAppTimeSlotType => {
    if (isSlotOutsideContractPeriod(privateSlot)) {
      return 'reserved'
    }

    switch (privateSlot.status) {
      case SlotStatus.OWN:
      case SlotStatus.OWN_RECURRING:
        return 'own'
      case SlotStatus.FREE:
        return 'free'
      default:
        return 'reserved'
    }
  }

  const reservationCalendarOverrides = {
    '--reservation-calendar-slot-min-height': '2.5rem',
    '--reservation-calendar-slot-width': '75px',
    '--spacing-2': '0.25rem',
  } as React.CSSProperties

  const residentDetails = pickResidentDetails(calendar, lang)

  return (
    <div>
      <div className="sticky top-28 py-2 bg-white z-10 -mx-1">
        <h3 className="text-lg font-semibold">{calendar.name}</h3>
      </div>

      <div style={reservationCalendarOverrides}>
        <ReservationCalendar
          dayHeaderStickyTop="150px"
          slotSizeMinutes={slotSizeMinutes}
          dayStartTime={dayStartTime}
          dayEndTime={dayEndTime}
        >
          {weekdays.map(({ weekday, weekdayName, privateSlots }) => {
            const sortedSlots = privateSlots.sort((a, b) =>
              a.startsAt.localeCompare(b.startsAt)
            )

            return (
              <ReservationCalendarDay
                key={`weekday-${weekday}`}
                title={weekdayName}
              >
                {sortedSlots.map((slot) => {
                  const startTime = formatTime(slot.startsAt)
                  const endTime = formatTime(slot.endsAt)

                  const slotType = getTimeSlotType(slot)

                  return (
                    <ResidentAppSlot
                      selected={
                        slot.timeSlots.some((s) =>
                          selectedTimeSlotsId?.includes(s.id)
                        ) ||
                        slot.timeSlots.some(
                          (s) =>
                            s.id === singleReservationModalState.timeSlot?.id
                        ) ||
                        slot.timeSlots.some(
                          (s) => s.id === cancelModalState.timeSlot?.id
                        )
                      }
                      key={slot.id}
                      startTime={startTime}
                      endTime={endTime}
                      slotType={slotType}
                      onClick={() => handlePrivateSlotClick(slot)}
                      single={slot.status === 'own'}
                      additionalDetails={
                        slot.timeSlots[0].reservations[0]?.additionalDetails ??
                        undefined
                      }
                    />
                  )
                })}
              </ReservationCalendarDay>
            )
          })}
        </ReservationCalendar>
      </div>

      <div
        style={
          {
            '--brand-2': 'var(--calendar-reservation-free-background-color)',
            '--brand-5': 'var(--calendar-reservation-own-background-color)',
            '--brand-6':
              'var(--calendar-reservation-own-background-color-hover)',
          } as React.CSSProperties
        }
      >
        <StatusLegend className="mt-4" direction="row">
          <StatusLegend.Item status="default" srLabel={t('legend.freeSlot')}>
            {t('legend.freeSlot')}
          </StatusLegend.Item>

          <StatusLegend.Item
            status="disabled"
            srLabel={t('legend.reservedSlot')}
          >
            {t('legend.reservedSlot')}
          </StatusLegend.Item>

          <StatusLegend.Item
            status="active"
            srLabel={t('legend.ownReservation')}
          >
            {t('legend.ownReservation')}
          </StatusLegend.Item>

          <StatusLegend.Item
            status="single-reservations"
            srLabel={t('legend.ownSingleReservation')}
          >
            {t('legend.ownSingleReservation')}
          </StatusLegend.Item>
        </StatusLegend>
      </div>

      <ResidentDetailsHtml html={residentDetails} className="mt-8" />

      <CancelReservationModal
        show={cancelModalState.show}
        onClose={() => setCancelModalState({ show: false, timeSlot: null })}
        calendar={calendar}
        timeSlot={cancelModalState.timeSlot}
        contractUuid={currentContractId}
        readonly={readonly}
      />

      <SingleReservationModal
        show={singleReservationModalState.show}
        onClose={() =>
          setSingleReservationModalState({ show: false, timeSlot: null })
        }
        calendar={calendar}
        timeSlot={singleReservationModalState.timeSlot}
        readonly={readonly}
      />
    </div>
  )
}
